/**
 * @graphql agg
 */

import type { MemoizedFn } from 'memoize-one';
import memoizeOne from 'memoize-one';
import isEqual from 'lodash/isEqual';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import type { InlineCommentState, AnnotationState } from '@atlaskit/editor-plugins/annotation';
// actually an enum not a type
import { AnnotationMarkStates, AnnotationTypes } from '@atlaskit/adf-schema/annotation';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { getLogger } from '@confluence/logger';
import { expVal } from '@confluence/feature-experiments';
import type { InlineCommentLocation } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.ts';
import type {
	AnnotationStateNode,
	AnnotationStateQueryType,
	AnnotationStateQueryVariables,
} from '@confluence/inline-comments-queries/entry-points/AnnotationStateQuery.ts';
import { AnnotationStateQuery } from '@confluence/inline-comments-queries/entry-points/AnnotationStateQuery.graphql.ts';
import { MAX_COMMENTS_TO_RETURN } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { buildDefaultCommentsContentStatusFilters } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { getProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import type {
	stateResolverPreloadAnnotationsDataQuery$data,
	stateResolverPreloadAnnotationsDataQuery$variables,
} from './__generated__/stateResolverPreloadAnnotationsDataQuery.graphql.ts';

export const preloadAnnotationsDataQuery: GraphQLTaggedNode = graphql`
	query stateResolverPreloadAnnotationsDataQuery(
		$pageId: ID!
		$contentStatus: [GraphQLContentStatus]
		$first: Long!
	) {
		comments(
			pageId: $pageId
			contentStatus: $contentStatus
			confluenceCommentFilter: { commentState: UNRESOLVED, commentType: INLINE }
			type: [INLINE]
			depth: ROOT
			first: $first
		) {
			nodes {
				id
				contentStatus
				location {
					__typename
					type
					... on InlineComment {
						inlineMarkerRef
						inlineResolveProperties {
							resolved
						}
					}
				}
			}
			totalCount
			__typename
		}
	}
`;

/**
 * Builds query variables for preloading annotation state
 * @param contentId - The ID of the content
 * @param pageMode - The page mode (VIEW, EDIT, or LIVE) to determine content status filters
 */
export const buildPreloadAnnotationsDataQueryVariables = (
	contentId: string,
	pageMode: PageMode,
): stateResolverPreloadAnnotationsDataQuery$variables => ({
	pageId: contentId,
	contentStatus: buildDefaultCommentsContentStatusFilters(pageMode),
	first: MAX_COMMENTS_TO_RETURN,
});

const logger = getLogger('inline-comments-common:state-resolver');
// TODO: Create a shared editor/renderer function

type GetPreloadAwareAnnotationsDataProps = {
	contentId: string;
	pageMode: PageMode;
};

const getPreloadAwareAnnotationsData = async ({
	contentId,
	pageMode,
}: GetPreloadAwareAnnotationsDataProps): Promise<AnnotationStateNode[]> => {
	if (!contentId) {
		return [];
	}

	// Check if processed comments data is available
	const processedData = getProcessedCommentsDataState();

	// If data is processed, use it directly
	if (processedData.isDataProcessed) {
		return processedData.openInlineComments as AnnotationStateNode[];
	}

	// Data not processed yet - fetch fresh data directly with network-only
	// This is simpler (albeit slightly slower) than preloading and guarantees fresh data
	// If we do preload in the future - keep in mind that we have to satisfy the following use cases
	// 1. We load in SSR and preload fails --> no cache data
	// 2. We load in SPA and preload hasn't completed before we check the cache value
	// 3. We load in SPA and have a stale value in the cache
	// If preloading you can also show stale data initially and refresh by manually updating annotations states
	// However if you do this, be careful for integrations w/ focused comments
	try {
		const result = await queryInterop<stateResolverPreloadAnnotationsDataQuery$data>({
			query: preloadAnnotationsDataQuery,
			variables: buildPreloadAnnotationsDataQueryVariables(contentId, pageMode),
			fetchPolicy: 'network-only',
		});

		return (result?.data?.comments?.nodes?.filter((node) => node?.location) ||
			[]) as AnnotationStateNode[];
	} catch {
		return [];
	}
};

type RendererAnnotationMap = {
	[key: string]: number;
};

// TODO: Create a shared editor/renderer function
export const mapRendererAnnotationStates: MemoizedFn<
	(
		annotations: string[],
		validAnnotationsCallback: (validAnnotations: string[]) => void,
		states?: AnnotationStateNode[],
		reactionMarkerRefs?: string[],
		optimisticResolvedMarkerRefs?: string[],
	) => AnnotationState<AnnotationTypes, AnnotationMarkStates>[]
> = memoizeOne(
	(
		annotations: string[],
		validAnnotationsCallback: (validAnnotations: string[]) => void,
		states?: AnnotationStateNode[],
		reactionMarkerRefs?: string[],
		optimisticResolvedMarkerRefs?: string[],
	): AnnotationState<AnnotationTypes, AnnotationMarkStates>[] => {
		const rendererAnnotationMap: RendererAnnotationMap = {};
		const annotationState: Array<AnnotationState<AnnotationTypes, AnnotationMarkStates>> = [];
		const optimisticResolvedMarkerRefSet = new Set(optimisticResolvedMarkerRefs);

		annotations.forEach((id, idx) => {
			// this will override any repeat ids given by the Renderer
			rendererAnnotationMap[id] = idx;
			annotationState.push({
				annotationType: AnnotationTypes.INLINE_COMMENT,
				id,
				state: AnnotationMarkStates.RESOLVED,
			});
		});

		states?.forEach((state) => {
			const annotationId = (state.location as InlineCommentLocation).inlineMarkerRef;
			const isResolved =
				Boolean((state.location as InlineCommentLocation)?.inlineResolveProperties?.resolved) ||
				(annotationId ? optimisticResolvedMarkerRefSet.has(annotationId) : false);

			// mark all unresolved annotations to be displayed by the renderer
			if (!isResolved) {
				if (annotationId) {
					const index = rendererAnnotationMap[annotationId];

					/**
					 * If the BE returns an annotation that the Renderer did not
					 * provide, we could error here. Also account for 0 index.
					 */
					if (index || index === 0) {
						annotationState[index].state = AnnotationMarkStates.ACTIVE;
					}
				}
			}
		});

		// Mark inline reaction annotations as ACTIVE so they render as highlights
		reactionMarkerRefs?.forEach((markerRef) => {
			const index = rendererAnnotationMap[markerRef];
			if ((index || index === 0) && !optimisticResolvedMarkerRefSet.has(markerRef)) {
				annotationState[index].state = AnnotationMarkStates.ACTIVE;
			}
		});

		const validAnnotations = annotations.filter((annotation) => {
			const index = rendererAnnotationMap[annotation];

			return annotationState[index].state === AnnotationMarkStates.ACTIVE;
		});

		// Record the valid annotations to use for navigation and query params
		validAnnotationsCallback(validAnnotations);

		return annotationState;
	},
	isEqual,
);

export const _mapAnnotationStates: MemoizedFn<
	(
		annotations: any,
		states: any,
		updateUnresolvedInlineComment: any,
		optimisticResolvedMarkerRefs?: string[],
	) => AnnotationState<AnnotationTypes, InlineCommentState>[]
> = memoizeOne(
	(
		annotations: any,
		states: any,
		updateUnresolvedInlineComment: any,
		optimisticResolvedMarkerRefs?: string[],
	): AnnotationState<AnnotationTypes, InlineCommentState>[] => {
		/**
		 * Build a map of annotation states with a default of true for resolved.
		 * This is to accommodate deleted annotations that still exist in the ADF.
		 */
		const editorAnnotationMap: any = {};
		const annotationState: Array<AnnotationState<AnnotationTypes, InlineCommentState>> = [];
		const optimisticResolvedMarkerRefSet = new Set(optimisticResolvedMarkerRefs);

		annotations.forEach((id: string, idx: number) => {
			// this will override any repeat ids given by the Editor
			editorAnnotationMap[id] = idx;
			annotationState.push({
				annotationType: AnnotationTypes.INLINE_COMMENT,
				id,
				state: {
					resolved: true,
				},
			});
		});

		states.forEach((state: any) => {
			const annotationId = state?.location?.inlineMarkerRef || '';
			const isResolved =
				Boolean(state?.location?.inlineResolveProperties?.resolved) ||
				(annotationId ? optimisticResolvedMarkerRefSet.has(annotationId) : false);

			// mark all unresolved annotations to be displayed by the editor
			if (!isResolved) {
				const index = editorAnnotationMap[annotationId];

				/**
				 * If the BE returns an annotation that the Editor did not
				 * provide, we could error here. Also account for 0 index.
				 */
				if (index || index === 0) {
					annotationState[index].state.resolved = false;
				}
			}
		});

		//Ordered list of unresolved inline comments
		const unresolvedInlineComments = annotations.filter((annotationId: string) => {
			const index = editorAnnotationMap[annotationId];
			if (!annotationState[index].state.resolved) {
				return annotationId;
			}
		});

		updateUnresolvedInlineComment(unresolvedInlineComments);
		return annotationState;
	},
	isEqual,
);

// TODO: Create a shared editor/renderer function
export const getEditorResolvedState = async ({
	annotations,
	pageId,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
	optimisticResolvedMarkerRefs,
}: {
	annotations: string[];
	pageId: string;
	triggerAnalyticsEvent: any;
	updateUnresolvedInlineComment: any;
	optimisticResolvedMarkerRefs?: string[];
}): Promise<AnnotationState<AnnotationTypes, InlineCommentState>[]> => {
	if (!annotations.length) {
		updateUnresolvedInlineComment([]);
		return Promise.resolve([]);
	}

	try {
		let annotationStateNodes: AnnotationStateNode[] | undefined;

		// Check if we should use the new comments data manager flow
		if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
			// NEW FLOW: Use preload-aware annotations data utility
			annotationStateNodes = await getPreloadAwareAnnotationsData({
				contentId: pageId,
				pageMode: PageMode.LIVE, // same as PageMode.EDIT for our case
			});
		} else {
			// OLD FLOW: Direct GraphQL query (fallback when experiment is disabled)

			const data = await queryInterop<AnnotationStateQueryType, AnnotationStateQueryVariables>({
				query: AnnotationStateQuery,
				fetchPolicy: 'network-only',
				variables: {
					pageId,
					// @ts-ignore - Legacy contentStatus values passed before typecheck changes
					contentStatus: ['DRAFT', 'CURRENT'],
				},
			});

			// Filter null elements
			annotationStateNodes = data.data.comments?.nodes?.filter((node) => node !== null) as
				| AnnotationStateNode[]
				| undefined;
		}

		// Common logic: map annotation state nodes to editor states
		return _mapAnnotationStates(
			annotations,
			annotationStateNodes,
			updateUnresolvedInlineComment,
			optimisticResolvedMarkerRefs,
		);
	} catch (error) {
		// COMMENTS-211 - When this code executes sometimes the `triggerAnalyticsEvent` is passed in as a promise
		//                this causes this call to throw an error where `.fire()` is not defined and thus the
		//                code below it which handles the various expected errors thrown to not run and thus
		//                the errors are thrown up the stack as unhandled ui errors causing a lot of noise
		try {
			if (triggerAnalyticsEvent) {
				triggerAnalyticsEvent({
					data: {
						source: 'editorComponent',
						action: 'failed',
						actionSubject: 'annotationState',
						actionSubjectId: 'inlineComments',
						attributes: {
							errorName: error.name,
							errorMessage: error.message,
						},
					},
					type: 'sendOperationalEvent',
				}).fire();
			}
		} catch (e) {
			logger.error`Error when logging analytics event, ${e}`;
		}

		if (
			isUnauthorizedError(error) ||
			// CEMS-1343 - Some specific pages have annotations that have a "null" id because we
			//             still informed the editor on failed creation attempts
			error?.message.includes('java.lang.NullPointerException') ||
			// COMMENTS-211 - If we don't find any inline comments for the given markerRefList
			//               we should mark it handled and return an empty list to be consumed
			error?.message.includes('.NotFoundException')
		) {
			markErrorAsHandled(error);
			return [];
		} else {
			logger.error`Error when retrieving resolved status for marks, ${error}`;
			throw error;
		}
	}
};

let rendererAnnotationsIds: string[];

export const getRendererAnnotationStates = async ({
	annotations,
	pageId,
	triggerAnalyticsEvent,
	validAnnotationsCallback,
	isNestedRender,
	reactionMarkerRefs,
	optimisticResolvedMarkerRefs,
}: {
	annotations: string[];
	pageId: string;
	triggerAnalyticsEvent: any;
	validAnnotationsCallback: (validAnnotations: string[]) => void;
	isNestedRender: boolean;
	reactionMarkerRefs?: string[];
	optimisticResolvedMarkerRefs?: string[];
}): Promise<AnnotationState<AnnotationTypes, AnnotationMarkStates>[]> => {
	// We need to have the page level renderer annotations
	// When the page renders the value of isNestedRender is false as per PageContentRendererFabrics
	// Later, when the macro renders isNestedRender is true
	// so, we will hold the value of annotations for the page renderer when isNestedRender is false

	if (!annotations.length) {
		validAnnotationsCallback([]);
		return Promise.resolve([]);
	}

	if (!isNestedRender) {
		rendererAnnotationsIds = annotations;
	}

	try {
		let annotationStateNodes: AnnotationStateNode[] | undefined;

		// Check if we should use the new comments data manager flow
		if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
			// NEW FLOW: Use preload-aware annotations data utility
			annotationStateNodes = await getPreloadAwareAnnotationsData({
				contentId: pageId,
				pageMode: PageMode.VIEW,
			});
		} else {
			// OLD FLOW: Direct GraphQL query (fallback when experiment is disabled)
			const data = await queryInterop<AnnotationStateQueryType, AnnotationStateQueryVariables>({
				query: AnnotationStateQuery,
				fetchPolicy: isNestedRender ? 'cache-first' : 'network-only',
				variables: {
					pageId,
				},
			});

			// Filter null elements
			annotationStateNodes = data.data.comments?.nodes?.filter((node) => node !== null) as
				| AnnotationStateNode[]
				| undefined;
		}

		// Common logic: map annotation state nodes to renderer states
		return mapRendererAnnotationStates(
			rendererAnnotationsIds,
			validAnnotationsCallback,
			annotationStateNodes,
			reactionMarkerRefs,
			optimisticResolvedMarkerRefs,
		);
	} catch (error) {
		// COMMENTS-1131 - When this code executes sometimes the `triggerAnalyticsEvent` is passed in as a promise
		//                this causes this call to throw an error where `.fire()` is not defined and thus the
		//                code below it which handles the various expected errors thrown to not run and thus
		//                the errors are thrown up the stack as unhandled ui errors causing a lot of noise
		try {
			if (triggerAnalyticsEvent) {
				triggerAnalyticsEvent({
					source: 'rendererComponent',
					action: 'failed',
					actionSubject: 'annotationState',
					actionSubjectId: 'inlineComments',
					attributes: {
						...getCommentsDataManagerExperimentAttributes(),
						errorName: error.name,
						errorMessage: error.message,
					},
				}).fire();
			}
		} catch (e) {
			logger.error`Error when logging analytics event, ${e}`;
		}

		if (
			isUnauthorizedError(error) ||
			// CEMS-1343 - Some specific pages have annotations that have a "null" id because we
			//             still informed the editor on failed creation attempts
			error?.message.includes('java.lang.NullPointerException') ||
			// COMMENTS-211 - If we don't find any inline comments for the given markerRefList
			//               we should mark it handled and return an empty list to be consumed
			error?.message.includes('.NotFoundException')
		) {
			markErrorAsHandled(error);
			return [];
		} else {
			logger.error`Error when retrieving resolved status for marks, ${error}`;
			throw error;
		}
	}
};
